if (!customElements.get('wingpass-drop')) {
  customElements.define(
    'wingpass-drop',
    class WingpassDrop extends HTMLElement {
      interval = null;
      pollTimer = null;
      launchTimer = null;
      abortController = null;

      connectedCallback() {
        this.values = this.querySelector('[data-drop-live-values]');
        this.status = this.querySelector('[data-drop-status]');
        this.claimed = this.querySelector('[data-drop-claimed]');
        this.total = this.querySelector('[data-drop-total]');
        this.fill = this.querySelector('[data-drop-fill]');
        this.progress = this.querySelector('[role="progressbar"]');
        this.countdownWrap = this.querySelector('[data-drop-countdown-wrap]');
        this.countdown = this.querySelector('[data-drop-countdown]');
        const isPlaceholder = this.dataset.mode === 'placeholder';
        this.timezone = this.dataset.timezone || 'UTC';
        this.startAt = isPlaceholder ? null : this.resolveInstant(this.dataset.startAt, this.dataset.startAtUnix);
        // UTC is a refresh hint only. It never changes the existing purchase schedule.
        this.launchAt = isPlaceholder ? null : (this.parseUtcLaunch(this.dataset.launchAtUtc) ?? this.startAt);
        this.launchRefreshStarted = false;
        this.launchCycle = Symbol('launch');
        this.endAt = isPlaceholder ? null : this.resolveInstant(this.dataset.endAt, this.dataset.endAtUnix);
        this.previewEndAt = isPlaceholder ? this.parseDuration(this.dataset.previewCountdown) : null;

        this.updateValues(this.values);
        this.tick();
        this.interval = window.setInterval(() => this.tick(), 1000);

        if (this.dataset.mode === 'live') {
          const configuredSeconds = Number(this.dataset.pollSeconds);
          const seconds = Number.isFinite(configuredSeconds) && configuredSeconds > 0
            ? Math.min(300, Math.max(15, configuredSeconds)) : 15;
          this.pollTimer = window.setInterval(() => {
            if (this.launchAt != null && Date.now() >= this.launchAt && Date.now() <= this.launchAt + 15000) return;
            this.refreshInventory();
          }, seconds * 1000);
        }
      }

      disconnectedCallback() {
        window.clearInterval(this.interval);
        window.clearInterval(this.pollTimer);
        window.clearTimeout(this.launchTimer);
        this.abortController?.abort();
        this.abortController = null;
        this.launchCycle = null;
      }

      /**
       * Explicit UTC only; invalid/blank values retain the existing wall-clock schedule.
       * @param {string} value
       * @returns {number|null}
       */
      parseUtcLaunch(value) {
        const utc = value?.trim();
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(utc || '')) return null;
        const instant = Date.parse(utc);
        if (!Number.isFinite(instant)) return null;
        const normalized = utc.includes('.') ? utc : utc.replace('Z', '.000Z');
        return new Date(instant).toISOString() === normalized ? instant : null;
      }

      /** @param {number} attempt */
      async refreshAtLaunch(attempt = 0) {
        if (!this.isConnected || Date.now() - this.launchAt > 15000) return;
        const cycle = this.launchCycle;
        // Refresh immediately, then every three seconds through the first 15 seconds.
        if (attempt < 5 && Date.now() + 3000 <= this.launchAt + 15000) {
          this.launchTimer = window.setTimeout(() => {
            if (cycle === this.launchCycle) return this.refreshAtLaunch(attempt + 1);
          }, 3000);
        }
        await this.refreshInventory();
      }

      /**
       * Prefers an IANA zone (store or override). If the zone name is missing
       * or invalid, falls back to the unix timestamp Liquid computed in the
       * shop timezone via the date filter.
       *
       * @param {string} wall
       * @param {string} unixSeconds
       * @returns {number|null}
       */
      resolveInstant(wall, unixSeconds) {
        if (this.isValidTimeZone(this.timezone)) {
          const parsed = this.parseDate(wall);
          if (parsed != null) {
            return parsed;
          }
        }

        const seconds = Number(unixSeconds);
        if (unixSeconds && Number.isFinite(seconds)) {
          return seconds * 1000;
        }

        return this.parseDate(wall);
      }

      /**
       * @param {string} timezone
       * @returns {boolean}
       */
      isValidTimeZone(timezone) {
        if (!timezone || timezone === 'shop') {
          return false;
        }

        try {
          Intl.DateTimeFormat('en-US', { timeZone: timezone });
          return true;
        } catch {
          return false;
        }
      }

      /**
       * Resolves a wall-clock string such as `2026-09-10T12:00:00` against the
       * configured time zone, so daylight saving shifts are applied for us.
       *
       * @param {string} value
       * @returns {number|null}
       */
      parseDate(value) {
        if (!value) {
          return null;
        }

        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
        if (!match) {
          return null;
        }

        const [, year, month, day] = match;
        const time = value.slice(11);

        // A merchant can pick a day the month doesn't have, such as February 31,
        // which would otherwise roll silently into the next month.
        const lastDay = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
        const safeDay = String(Math.min(Number(day), lastDay)).padStart(2, '0');

        const naive = Date.parse(`${year}-${month}-${safeDay}T${time}Z`);
        if (Number.isNaN(naive)) {
          return null;
        }

        // Two passes so instants sitting near a DST transition settle correctly.
        let resolved = naive;
        for (let pass = 0; pass < 2; pass += 1) {
          resolved = naive - this.zoneOffset(resolved);
        }
        return resolved;
      }

      /**
       * @param {number} timestamp
       * @returns {number} Offset of the configured zone from UTC, in milliseconds.
       */
      zoneOffset(timestamp) {
        try {
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: this.timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });

          const parts = {};
          for (const part of formatter.formatToParts(new Date(timestamp))) {
            parts[part.type] = part.value;
          }

          const wall = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`;
          const zoned = Date.parse(wall);
          return Number.isNaN(zoned) ? 0 : zoned - timestamp;
        } catch {
          return 0;
        }
      }

      /**
       * @param {string} value
       * @returns {number|null}
       */
      parseDuration(value) {
        if (!value) {
          return null;
        }
        const match = value.trim().match(/^(\d{1,3}):(\d{2}):(\d{2})$/);
        if (!match) {
          return null;
        }

        const hours = Number(match[1]) * 3600;
        const minutes = Number(match[2]) * 60;
        const seconds = Number(match[3]);
        const durationSeconds = hours + minutes + seconds;
        const milliseconds = durationSeconds * 1000;
        return Date.now() + milliseconds;
      }

      /**
       * @param {HTMLElement|null} source
       */
      updateValues(source) {
        if (!source) {
          return;
        }

        this.remainingValue = Math.max(0, Number(source.dataset.remaining) || 0);
        this.variantId = source.dataset.variantId;
        this.variantAvailable = source.dataset.variantAvailable === 'true';
        this.claimedValue = Math.max(0, Number(source.dataset.claimed) || 0);
        this.totalValue = Math.max(1, Number(source.dataset.total) || 1);
        this.percentValue = Math.min(100, Math.max(0, Number(source.dataset.percent) || 0));

        if (this.claimed) {
          this.claimed.textContent = String(this.claimedValue);
        }
        if (this.total) {
          this.total.textContent = String(this.totalValue);
        }
        if (this.fill) {
          this.fill.style.setProperty('--wingpass-drop-progress', `${this.percentValue}%`);
        }

        if (this.progress) {
          this.progress.setAttribute('aria-valuemax', String(this.totalValue));
          this.progress.setAttribute('aria-valuenow', String(this.claimedValue));
        }
      }

      tick() {
        if (!this.isConnected) return;
        const now = Date.now();
        if (this.dataset.mode === 'live' && this.launchAt != null && now >= this.launchAt && !this.launchRefreshStarted) {
          this.launchRefreshStarted = true;
          if (now - this.launchAt <= 15000) this.refreshAtLaunch();
        }
        const isUpcoming = Boolean(this.startAt && now < this.startAt);
        const isExpired = Boolean(this.endAt && now >= this.endAt);
        const isSoldOut = this.remainingValue <= 0;
        let state = 'live';
        let target = this.endAt || this.previewEndAt;

        if (isUpcoming) {
          state = 'upcoming';
          target = this.startAt;
        } else if (isExpired || isSoldOut) {
          state = 'closed';
          target = null;
        }

        this.dataset.state = state;
        this.status.textContent = this.dataset.liveLabel;
        if (state === 'upcoming') {
          this.status.textContent = this.dataset.upcomingLabel;
        } else if (state === 'closed') {
          this.status.textContent = this.dataset.closedLabel;
        }

        this.updateCountdown(target, now);
        this.dispatchEvent(
          new CustomEvent('wingpass:drop-state', {
            bubbles: true,
            detail: { state, remaining: this.remainingValue, variantId: this.variantId, available: this.variantAvailable },
          }),
        );
      }

      /**
       * @param {number|null} target
       * @param {number} now
       */
      updateCountdown(target, now) {
        if (!this.countdownWrap || !this.countdown) {
          return;
        }

        if (!target) {
          this.countdownWrap.hidden = true;
          return;
        }

        this.countdownWrap.hidden = false;
        const totalSeconds = Math.max(0, Math.floor((target - now) / 1000));
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        this.countdown.textContent = `${hours}:${minutes}:${seconds}`;
      }

      async refreshInventory() {
        if (!this.isConnected || this.dataset.mode !== 'live') return false;
        this.abortController?.abort();
        this.abortController = new AbortController();
        const controller = this.abortController;

        const url = new URL(window.location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', 'wingpass-inventory');
        const product = this.closest('wingpass-product');
        const variantId = product?.querySelector('[data-variant-select]')?.value || product?.querySelector('[data-variant-input]')?.value;
        if (variantId) url.searchParams.set('variant', variantId);
        this.dataset.refreshStatus = 'loading';

        try {
          const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
          if (!response.ok) {
            throw new Error('Inventory endpoint request failed');
          }

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const source = documentFragment.querySelector('[data-drop-live-values]');
          if (!this.isConnected || controller !== this.abortController || controller.signal.aborted) return false;
          const remaining = Number(source?.dataset.remaining);
          if (!source || source.dataset.variantId !== variantId ||
              !['true', 'false'].includes(source.dataset.variantAvailable) ||
              !source.dataset.remaining?.trim() || !Number.isInteger(remaining) || remaining < 0) {
            throw new Error('Inventory endpoint returned invalid variant data');
          }
          const total = Math.max(this.totalValue, remaining, 1);
          const claimed = total - remaining;
          this.updateValues({ dataset: {
            ...source.dataset, total: String(total), claimed: String(claimed),
            percent: String(Math.round(claimed * 100 / total)),
          } });
          this.dataset.refreshStatus = 'ok';
          this.tick();
          return this.variantAvailable && this.remainingValue > 0;
        } catch {
          if (this.isConnected && controller === this.abortController && !controller.signal.aborted) {
            this.dataset.refreshStatus = 'error';
            this.dispatchEvent(new CustomEvent('wingpass:inventory-error', { bubbles: true }));
          }
          return false;
        } finally {
          if (this.abortController === controller) this.abortController = null;
        }
      }
    },
  );
}

if (!customElements.get('wingpass-product')) {
  customElements.define(
    'wingpass-product',
    class WingpassProduct extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.variantSelect = this.querySelector('[data-variant-select]');
        this.buyButton = this.querySelector('[data-buy-button]');
        this.cartForm = this.querySelector('ajax-cart-product-form');
        if (this.cartForm) {
          this.cartObserver = new MutationObserver(() => this.updateBuyButton());
          this.cartObserver.observe(this.cartForm, { attributes: true, attributeFilter: ['processing'] });
        }
        this.dropState = 'live';

        const { signal } = this.abortController;

        this.variantSelect?.addEventListener('change', () => this.updateVariant(), { signal });
        this.addEventListener('wingpass:drop-state', (event) => this.updateDropState(event.detail), { signal });
        this.updateVariant();
        const drop = this.querySelector('wingpass-drop');
        if (drop?.values) drop.tick();
      }

      disconnectedCallback() {
        this.abortController.abort();
        this.cartObserver?.disconnect();
      }

      updateVariant() {
        const option = this.variantSelect?.selectedOptions[0];
        const drop = this.querySelector('wingpass-drop[data-mode="live"]');
        if (!option) {
          this.variantAvailable = !this.buyButton?.disabled;
          drop?.refreshInventory();
          this.updateBuyButton();
          return;
        }

        this.variantAvailable = option.dataset.available === 'true';

        for (const input of this.querySelectorAll('[data-variant-input]')) {
          input.value = option.value;
        }

        const price = option.dataset.price || '';
        const priceDisplay = this.querySelector('[data-product-price]');
        const buttonPrice = this.querySelector('[data-buy-price]');
        if (priceDisplay) {
          priceDisplay.textContent = price;
        }
        if (buttonPrice) {
          buttonPrice.textContent = ` — ${price}`;
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
        if (drop) this.dropHasInventory = false;
        drop?.refreshInventory();
        this.updateBuyButton();
      }

      /**
       * @param {{ state: string, remaining: number, variantId: string, available: boolean }} detail
       */
      updateDropState(detail) {
        const selectedId = this.variantSelect?.value || this.querySelector('[data-variant-input]')?.value;
        if (detail.variantId !== selectedId) return;
        this.dropState = detail.state;
        this.dropHasInventory = detail.remaining > 0;
        this.variantAvailable = detail.available === true;
        const option = this.variantSelect?.selectedOptions[0];
        if (option) option.dataset.available = String(this.variantAvailable);
        if (this.querySelector('wingpass-drop[data-mode="live"]')) {
          for (const announcement of this.ownerDocument.querySelectorAll('[data-wingpass-announcement]')) {
            if (announcement.dataset.productId !== this.dataset.productId) continue;
            const text = this.variantAvailable && this.dropHasInventory
              ? announcement.dataset.liveLabel : announcement.dataset.closedLabel;
            // Avoid repeating an aria-live announcement on every countdown tick.
            if (announcement.textContent.trim() !== text) announcement.textContent = text;
          }
        }
        this.updateBuyButton();
      }

      updateBuyButton() {
        const showSoldOut = this.dropState !== 'upcoming'
          && (this.dropHasInventory === false || this.variantAvailable === false);
        for (const copy of this.querySelectorAll('[data-wingpass-sold-out-copy]')) copy.hidden = !showSoldOut;
        if (!this.buyButton) {
          return;
        }

        const label = this.buyButton.querySelector('[data-buy-label]');
        const isAvailable = this.variantAvailable !== false && this.dropHasInventory !== false;
        const busy = this.cartForm?.hasAttribute('processing') === true;
        const canBuy = isAvailable && this.dropState === 'live' && !busy;

        this.buyButton.disabled = !canBuy;
        this.buyButton.setAttribute('aria-busy', String(busy));
        const price = this.buyButton.querySelector('[data-buy-price]');
        if (price) price.hidden = showSoldOut;

        if (!label) {
          return;
        }
        if (busy) {
          label.textContent = 'Adding…';
        } else if (this.dropState === 'upcoming') {
          label.textContent = this.buyButton.dataset.upcomingLabel;
        } else if (showSoldOut) {
          label.textContent = this.buyButton.dataset.soldOutLabel;
        } else if (this.dropState === 'closed') {
          label.textContent = this.buyButton.dataset.closedLabel;
        } else if (isAvailable) {
          label.textContent = this.buyButton.dataset.availableLabel;
        } else {
          label.textContent = this.buyButton.dataset.soldOutLabel;
        }
      }
    },
  );
}
